import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircle,
  Sparkles,
  Mail,
  Clock,
  FileText,
  ArrowRight,
} from "lucide-react";

export default function DonePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const lang = i18n.language || "en";

  const handleBackHome = () => {
    navigate(`/${lang}`);
  };

  // Simple way to ensure URL language matches current i18n language
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const currentLangInPath = pathSegments[0];
  if (currentLangInPath !== lang && (currentLangInPath === "en" || currentLangInPath === "nl")) {
    navigate(`/${lang}/done`, { replace: true });
  }

  return (
    <div className="min-h-screen gradient-bg py-10 md:py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Top badge */}
        <div className="mb-6 md:mb-10">
          <button
            onClick={handleBackHome}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600">
              ←
            </span>
            <span>{t("done.backToHome", "Back to home")}</span>
          </button>
        </div>

        <div className="glass rounded-3xl subtle-shadow p-6 md:p-10 lg:p-12">
          <div className="grid lg:grid-cols-[1.1fr_minmax(0,1.2fr)] gap-10 lg:gap-14 items-start">
            {/* Left column: celebration copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-xs font-medium text-purple-700 mb-4">
                <Sparkles size={14} />
                <span>{t("done.badge", "Assessment completed")}</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight mb-4">
                {t("done.title", "You did it!")}
              </h1>

              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 md:mb-8 max-w-xl">
                {t(
                  "done.description",
                  "You’ve just done something most people never do: you’ve taken the time to truly understand yourself. That decision alone already sets your next chapter in motion."
                )}
              </p>

              <div className="space-y-4 mb-8 md:mb-10">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    {t(
                      "done.point1",
                      "Our AI is now connecting the dots across all your answers using proven principles from psychology, neuroscience and career science."
                    )}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    {t(
                      "done.point2",
                      "In a few minutes you’ll receive a clear, personalized roadmap that shows your strengths, blind spots and most aligned next moves."
                    )}
                  </p>
                </div>
              </div>

              <div className="glass rounded-2xl p-4 md:p-5 border border-purple-100 flex items-center gap-4 mb-6 md:mb-8 bg-white/80">
                <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      {t("done.emailHeading", "Check your email in about 10 minutes")}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600">
                    {t(
                      "done.emailBody",
                      "Your full Purpose Profile will be delivered straight to your inbox. If you don’t see it, please check your spam or promotions folder."
                    )}
                  </p>
                </div>
              </div>

              <button
                onClick={handleBackHome}
                className="premium-button text-white rounded-xl py-3.5 px-8 font-semibold text-sm md:text-base inline-flex items-center gap-2 hover-lift"
              >
                <span>{t("done.cta", "Return to homepage")}</span>
                <ArrowRight size={18} />
              </button>
            </div>

            {/* Right column: vertical step timeline */}
            <div className="border-l border-gray-200/80 pl-6 lg:pl-10 space-y-6 md:space-y-8 relative">
              {/* Timeline line decoration */}
              <div className="absolute left-0 top-1 bottom-1 hidden md:block">
                <div className="h-full w-px bg-gradient-to-b from-purple-200 via-gray-200 to-purple-200" />
              </div>

              {/* Step 1 */}
              <div className="relative">
                <div className="absolute -left-6 md:-left-8 top-4 w-3 h-3 rounded-full bg-purple-600 border-4 border-purple-100 shadow-sm" />
                <div className="glass rounded-2xl p-5 md:p-6 bg-green-50/80 border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold tracking-wide text-green-700 uppercase">
                      {t("done.step1Label", "Step 1")}
                    </p>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    {t("done.step1Title", "Share your story")}
                  </h2>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {t(
                      "done.step1Body",
                      "Completed! Your thoughtful answers gave us deep insight into what truly drives you, your hidden strengths and untapped potential."
                    )}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="absolute -left-6 md:-left-8 top-4 w-3 h-3 rounded-full bg-purple-600 border-4 border-purple-100 shadow-sm" />
                {/* Deliberately not using `glass` here so the dark gradient stays rich and text is high-contrast */}
                <div className="rounded-2xl p-5 md:p-6 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold tracking-wide text-purple-100 uppercase flex items-center gap-2">
                      {t("done.step2Label", "Step 2")}
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px]">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        {t("done.step2Pill", "Happening now")}
                      </span>
                    </p>
                    <Sparkles className="w-5 h-5 text-purple-50" />
                  </div>
                  <h2 className="text-lg md:text-xl font-semibold mb-2 text-white">
                    {t("done.step2Title", "Deep AI analysis")}
                  </h2>
                  <p className="text-sm text-purple-50 leading-relaxed">
                    {t(
                      "done.step2Body",
                      "Right now, our AI is analyzing your responses using advanced principles from psychology, neuroscience and career science to reveal patterns you might miss yourself."
                    )}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="absolute -left-6 md:-left-8 top-4 w-3 h-3 rounded-full bg-purple-600 border-4 border-purple-100 shadow-sm" />
                <div className="glass rounded-2xl p-5 md:p-6 bg-white/80">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold tracking-wide text-purple-700 uppercase">
                      {t("done.step3Label", "Step 3")}
                    </p>
                    <Clock className="w-5 h-5 text-purple-500" />
                  </div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    {t("done.step3Title", "Get your action plan")}
                  </h2>
                  <p className="text-sm text-gray-700 leading-relaxed mb-2">
                    {t(
                      "done.step3Body1",
                      "In about 10 minutes you’ll receive your 15–20 page Purpose Profile: your values, strengths, blind spots, limiting beliefs and concrete next steps."
                    )}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                    {t(
                      "done.step3Body2",
                      "You’ll also find instructions to book your optional 60‑minute coaching session if you want to go even deeper."
                    )}
                  </p>
                </div>
              </div>

              {/* Footer hint */}
              <div className="pt-2">
                <div className="inline-flex items-center gap-2 text-xs text-gray-500 bg-white/60 px-3 py-1.5 rounded-full">
                  <FileText className="w-4 h-4 text-purple-500" />
                  <span>
                    {t(
                      "done.footer",
                      "If your profile hasn’t arrived after 15 minutes, reply to your purchase confirmation email and we’ll help you out."
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


